/**
 * Las piezas que muestran lo que cambia declarar (o heredar) el consentimiento de la base (decisión de Alan del
 * 29/10/2026). Las usan «Listo» de la subida (`DeclaracionEnListo`) y el historial (`ConsentimientoDeLaBase`):
 * - `AlcanceDeclaracion`: a cuántos clientes les vuelve «sí» y por qué no a los demás (el criterio del envío);
 * - `PreparadasDeNuevo`: las campañas ya preparadas que no toman sola la declaración (el sí se fija al preparar), con
 *   «Preparar de nuevo» (#185) para las de esta base y el link a sus Mensajes para las de la anterior;
 * - `ChipRespaldo`: «con respaldo» (y «Ver respaldo», sólo el equipo) o «sin respaldo» para las de antes.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileCheck2, FileWarning, Loader2, RefreshCw } from "lucide-react";
import { baseVentasApi } from "../api";
import type { AlcanceDeLaDeclaracion, ConsentimientoDeclarado, PreparadaConLaBase } from "../types";
import { cuandoSubida } from "../logic/base";
import { textosAlcance } from "../logic/respaldo";
import { enviosApi } from "../../envios/api";
import { rutaMensajes } from "../../mensajes/logic/mensajes";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { leerError, leerRespuesta, textoError } from "../../wa-oficial/logic/respuesta";

export function AlcanceDeclaracion({ alcance }: { alcance: AlcanceDeLaDeclaracion }) {
  const t = textosAlcance(alcance);
  return (
    <div className="rounded-lg bg-white px-3 py-2 text-sm text-slate-700" data-testid="alcance-declaracion">
      <p className="font-semibold text-slate-900">{t.titular}</p>
      {t.detalle.length > 0 && (
        <ul className="mt-1 list-disc pl-5 text-xs text-slate-600">
          {t.detalle.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ChipRespaldo({ c, versionId }: { c: ConsentimientoDeclarado; versionId: string }) {
  const equipo = useEsEquipoDeenex();
  const [bajando, setBajando] = useState(false);
  const [error, setError] = useState("");
  if (!c.conRespaldo || !c.respaldo) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800" data-testid="chip-respaldo">
        <FileWarning size={12} aria-hidden="true" /> Sin respaldo
      </span>
    );
  }
  const r = c.respaldo;
  const bajar = async () => {
    setBajando(true);
    setError("");
    try {
      await baseVentasApi.descargarRespaldo({ id: versionId }, r.nombre);
    } catch (e) {
      setError(e instanceof Error && !(e instanceof TypeError) && e.message ? e.message : textoError(leerError(e)));
    } finally {
      setBajando(false);
    }
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800" data-testid="chip-respaldo">
        <FileCheck2 size={12} aria-hidden="true" /> Con respaldo
      </span>
      {/* el respaldo trae el mail o el chat del comercio: lo baja sólo el equipo */}
      {equipo && (
        <button
          type="button"
          onClick={() => void bajar()}
          disabled={bajando}
          className="min-h-[44px] rounded-lg border border-emerald-200 bg-white px-3 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {bajando ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Download size={13} aria-hidden="true" />} Ver respaldo
        </button>
      )}
      {error && <span role="alert" className="text-xs text-red-700">{error}</span>}
    </span>
  );
}

function PreparadaDeEstaBase({ p }: { p: PreparadaConLaBase }) {
  const equipo = useEsEquipoDeenex();
  const [estado, setEstado] = useState<"" | "preparando" | "lista">("");
  const [error, setError] = useState("");
  const preparar = async () => {
    setEstado("preparando");
    setError("");
    try {
      const l = leerRespuesta(await enviosApi.preparar(p.id));
      if (!l.ok) {
        setError(l.error || textoError(l));
        setEstado("");
        return;
      }
      setEstado("lista");
    } catch (e) {
      setError(textoError(leerError(e)));
      setEstado("");
    }
  };
  return (
    <li className="flex flex-wrap items-center gap-2">
      <Link to={rutaMensajes(p.id)} className="font-semibold text-violet-700 underline">{p.nombre || "Campaña sin nombre"}</Link>
      <span className="text-slate-500">preparada el {cuandoSubida(p.preparadaAt)}</span>
      {estado === "lista" ? (
        <span className="font-semibold text-emerald-700">Preparada de nuevo: ya toma la declaración.</span>
      ) : equipo ? (
        <button
          type="button"
          onClick={() => void preparar()}
          disabled={estado === "preparando"}
          className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {estado === "preparando" ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <RefreshCw size={13} aria-hidden="true" />} Preparar de nuevo
        </button>
      ) : null}
      {error && <span role="alert" className="text-red-700">{error}</span>}
    </li>
  );
}

/**
 * `heredada`: lo que se contestó en «Listo» a «¿sigue valiendo?» (true «Sí», false «No»); sin contestar, undefined.
 * Cambia sólo el texto de las campañas preparadas con la versión anterior.
 */
export function PreparadasDeNuevo({ preparadas, heredada }: { preparadas: PreparadaConLaBase[]; heredada?: boolean }) {
  if (!preparadas.length) return null;
  const deEsta = preparadas.filter((p) => !p.conLaBaseAnterior);
  const deLaAnterior = preparadas.filter((p) => p.conLaBaseAnterior);
  return (
    <div className="space-y-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="preparadas-de-nuevo">
      {deEsta.length > 0 && (
        <div>
          <p>Estas campañas se prepararon antes de la declaración y no la toman solas: preparalas de nuevo para que les llegue el sí.</p>
          <ul className="mt-1 space-y-1">
            {deEsta.map((p) => (
              <PreparadaDeEstaBase key={p.id} p={p} />
            ))}
          </ul>
        </div>
      )}
      {deLaAnterior.length > 0 && (
        <div>
          <p>
            {heredada === false
              ? "Estas campañas se prepararon con la versión anterior y con su declaración, que ya no vale: no las mandes así. Segmentalas de nuevo con esta base."
              : "Estas campañas se prepararon con la versión anterior: para que usen esta base (y esta declaración), segmentalas de nuevo."}
          </p>
          <ul className="mt-1 space-y-1">
            {deLaAnterior.map((p) => (
              <li key={p.id}>
                <Link to={rutaMensajes(p.id)} className="font-semibold text-violet-700 underline">{p.nombre || "Campaña sin nombre"}</Link>
                <span className="text-amber-800"> · preparada el {cuandoSubida(p.preparadaAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
